import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../components/AuthContext';
import NPSPage from '../components/NPSPage';
import LeaverJoinerPlot from '../components/LeaverJoinerPlot';
import CompetitorChurnPlot from '../components/CompetitorChurnPlot';
import PricingPlot from '../components/PricingPlot';
import NoDataMessage from '../components/NoDataMessage';
import FilteredPostsDisplay from '../components/FilteredPostsDisplay';
import ContentCard from '../components/ContentCard';
import CompanyTwitterSearch from '../components/CompanyTwitterSearch';
import CompanyAdminPanel from '../components/CompanyAdminPanel';
import CompetitiveDifferentiators from '../components/CompetitiveDifferentiators';
import CompetitorMentionsChart from '../components/CompetitorMentionsChart';
import CompanyNews from '../components/CompanyNews';
import CustomAssessmentDisplay from '../components/CustomAssessmentDisplay';
import '../styles/CompanyPage.css';
import { validateRecentlyViewedCompanies } from '../utils/searchUtils';
import { mergeCompetitiveAssessments } from '../utils/competitiveAssessments';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CompanySearch from '../components/CompanySearch';
import NPSComparisonChart from '../components/NPSComparisonChart';
import CompanyChat from '../components/CompanyChat';
import InterestRateDisplay from '../components/InterestRateDisplay';
import DemographicEvaluator from '../components/DemographicEvaluator';
import LocationInsights from '../components/LocationInsights';
import PostVolumePanel from '../components/PostVolumePanel';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Bar } from 'recharts';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import InterestLevelChart from '../components/InterestLevelChart';
import CustomSentimentBreakdown from '../components/CustomSentimentBreakdown';
import DailyLeaverJoiner from '../components/DailyLeaverJoiner';
import PostAnalytics from '../components/PostAnalytics';
import PostLikesAnalytics from '../components/PostLikesAnalytics';
import PostEngagementAnalytics from '../components/PostEngagementAnalytics';
import PostSentimentEngagementAnalytics from '../components/PostSentimentEngagementAnalytics';
import NPSMovingAverage from '../components/NPSMovingAverage';
import PricingTrendChart from '../components/PricingTrendChart';
import SatisfactionReasons from '../components/SatisfactionReasons';
import { apiPost } from '../services/apiClient';
import ReportGenerator from '../components/ReportGenerator/ReportGenerator';
import CreditsOverview from '../components/Credits/CreditsOverview';
import DimensionNPSPage from '../components/DimensionNPSPage';
import LLMRecommendationsPage from '../components/LLMRecommendationsPage';
// Old YouTube Analytics — retained for reference, no longer routed (see components/YouTubeAnalytics.js)
// import YouTubeAnalytics from '../components/YouTubeAnalytics';
import YouTubeAnalyticsNew from '../components/YouTubeAnalyticsNew';
import LLMCompetitiveComparisonPage from '../components/LLMCompetitiveComparisonPage';
import DimensionDashboards from '../components/DimensionDashboards';
import DimensionDashboardsComposed from '../components/DimensionDashboardsComposed';


const CompanyPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [companyData, setCompanyData] = useState({});
    const [onWatchlist, setOnWatchlist] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { userId, token, serverAddress, flaskServerAddress, userDoesNotExist, isLoading: authLoading } = useAuth();
    const [filings, setFilings] = useState(null);
    const [products, setProducts] = useState([]);
    const [stockChartTimeRange, setStockChartTimeRange] = useState('3y');
    const [focusedItem, setFocusedItem] = useState(null);
    const sidebarRef = useRef(null);
    const visitLoggedRef = useRef(false);
    const [competitiveComparisons, setCompetitiveComparisons] = useState({});
    const [npsData, setNPSData] = useState([]);
    const [topicMap, setTopicMap] = useState({});
    const [locationTopicIds, setLocationTopicIds] = useState([]);
    const [relevantPosts, setRelevantPosts] = useState([]);
    const [allRelevantPosts, setAllRelevantPosts] = useState([]);
    const [interestRateData, setInterestRateData] = useState(null);
    const [isLoadingInterestRates, setIsLoadingInterestRates] = useState(false);
    const [interestChangeData, setInterestChangeData] = useState([]);
    const [isLoadingInterestChange, setIsLoadingInterestChange] = useState(false);
    const [chartSettings, setChartSettings] = useState({
        threshold: -5,
        operator: '<',
        showRawCounts: false,
        displayMode: 'threshold' // 'threshold' or 'distribution'
    });
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [showPostDisplay, setShowPostDisplay] = useState(false);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(null);
    const [dailyTrackerType, setDailyTrackerType] = useState('leaver-joiner');
    // Sticky product/topic selection shared across chart sections on the
    // page, so switching between chart types (or sections) doesn't reset
    // the selected topic. Each chart component only adopts this value when
    // it's valid for its own (sometimes narrower) set of topics; otherwise
    // it falls back to its own default.
    const [sharedSelectedTopic, setSharedSelectedTopic] = useState('');
    const [reportModalOpen, setReportModalOpen] = useState(false);

    // Track which data types have been loaded (for lazy loading)
    const [loadedDataTypes, setLoadedDataTypes] = useState({
        nps: false,
        pricing: false,
        leaverJoiner: false,
        competitiveComparison: false
    });

    const isAdmin = userId === '0f482aef-f510-46a1-b811-59d2ed4859f3';

    // Redirect to home if user is not logged in (but wait for auth to initialize first)
    useEffect(() => {
        if (!authLoading && userDoesNotExist) {
            sessionStorage.setItem('session_expired', 'true');
            navigate('/');
        }
    }, [authLoading, userDoesNotExist, navigate]);

    // Define custom assessment metrics
    const customAssessmentMetrics = [
        { id: 'price_difference', label: 'Price Difference Analysis' },
        { id: 'dealership_tactics', label: 'Dealership Tactics' },
        { id: 'customer_preference', label: 'Customer Preference' }
    ];

    // Add function to check for custom assessment data
    const hasCustomAssessmentData = (posts, metricName) => {
        if (!posts || !Array.isArray(posts) || posts.length === 0) {
            return false;
        }

        // Count posts with non-null ratings for this metric
        const postsWithData = posts.filter(post => 
            post?.post_assessments?.some(assessment => 
                assessment?.type === metricName && 
                assessment?.rating !== null
            )
        );

        // Only return true if we have at least 5 posts with data
        return postsWithData.length >= 5;
    };

    // Add function to get custom assessment data
    const getCustomAssessmentData = (posts, metricName) => {
        if (!posts || !Array.isArray(posts)) return [];
        
        return posts
            .filter(post => 
                post?.post_assessments?.some(assessment => 
                    assessment?.type === metricName && 
                    assessment?.rating !== null
                )
            )
            .map(post => {
                const assessment = post.post_assessments.find(a => a.type === metricName);
                return {
                    ...post, // Preserve all original post fields
                    rating: assessment.rating,
                    confidence: assessment.confidence,
                    explanation: assessment.explanation,
                    credibility: assessment.credibility,
                    comparator: assessment.comparator || '',
                    details: assessment.details || {},
                    ratingName: metricName,
                    ratingType: metricName,
                    ratingFormat: metricName === 'price_difference' ? 'currency' : 'outOfTen'
                };
            })
            .sort((a, b) => (b.time || 0) - (a.time || 0));
    };

    // // Log company visit
    // useEffect(() => {
    //     const logVisit = async () => {
    //         console.log('Attempting to log visit:', { id, userId, visitLogged: visitLoggedRef.current });
    //         if (!id || !userId || visitLoggedRef.current) return;
            
    //         try {
    //             const response = await fetch(serverAddress + 'log-company-visit', {
    //                 method: 'POST',
    //                 headers: {
    //                     'Content-Type': 'application/x-www-form-urlencoded',
    //                 },
    //                 body: new URLSearchParams({
    //                     company_id: id,
    //                     user_id: userId,
    //                     token: token,
    //                     visit_source: location.state?.source || 'direct'
    //                 }),
    //             });
                
    //             if (!response.ok) {
    //                 throw new Error('Failed to log visit');
    //             }
                
    //             console.log('Visit logged successfully');
    //             visitLoggedRef.current = true;
    //         } catch (error) {
    //             console.error('Failed to log company visit:', error);
    //         }
    //     };

    //     logVisit();
    // }, [id, userId, token, location, serverAddress]);

    // Get the active section from URL or default to 'overview'
    const getActiveSectionFromUrl = () => {
        const params = new URLSearchParams(location.search);
        const section = params.get('section') || 'overview';
        console.log('Current section from URL:', section);
        return section;
    };

    const [activeSection, setActiveSection] = useState(getActiveSectionFromUrl());

    // Update URL when section changes
    const handleSectionChange = (sectionId) => {
        console.log('Changing section to:', sectionId);
        setActiveSection(sectionId);
        setFocusedItem(sectionId);
        navigate(`/company/${id}?section=${sectionId}`, { replace: true });
        // Reset scroll position when changing sections
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Update active section when URL changes
    useEffect(() => {
        setActiveSection(getActiveSectionFromUrl());
    }, [location.search]);

    // Get all posts from topics
    const allPosts = useMemo(() => {
        return companyData.topics?.flatMap(topic => topic.posts) || [];
    }, [companyData.topics]);

    // Competitive comparison rows pooled from both sides of each pair: each
    // topic's own rows plus the sign-flipped rows its competitors hold about
    // it, so head-to-head numbers mirror what the competitor's page shows.
    const competitiveAssessmentData = useMemo(
        () => mergeCompetitiveAssessments(topicMap),
        [topicMap]
    );

    // Create custom assessment section based on available data
    const customAssessmentSection = useMemo(() => {
        const subsections = customAssessmentMetrics
            .filter(metric => hasCustomAssessmentData(allPosts, metric.id))
            .map(metric => ({
                id: `custom-${metric.id}`,
                label: metric.label
            }));

        return subsections.length > 0 ? {
            id: 'custom-assessments',
            label: 'Custom Assessments',
            subsections
        } : null;
    }, [allPosts]);

    // Topics that actually have location data, for the Location Insights product selector
    const locationTopicMap = useMemo(() => {
        const filtered = {};
        for (const topicId of locationTopicIds) {
            if (topicMap[topicId]) filtered[topicId] = topicMap[topicId];
        }
        return filtered;
    }, [topicMap, locationTopicIds]);

    // Update sidebar sections to include custom assessment section if available
    const sidebarSections = useMemo(() => {
        const sections = [
            { 
                id: 'overview', 
                label: 'Company Overview',
                subsections: [
                    { id: 'products', label: 'Products' },
                    ...(companyData.ticker ? [
                        // { id: 'financials', label: 'Financials' },
                        // { id: 'earnings', label: 'Earnings History' },
                        // { id: 'filings', label: 'Company Filings' },
                        // { id: 'transcripts', label: 'Earnings Transcripts' }
                    ] : [])
                ]
            },
            {
                id: 'behavior',
                label: 'Customer Behavior',
                subsections: [
                    { id: 'nps', label: 'Estimated NPS' },
                    { id: 'dimension-nps', label: 'NPS by Dimension' },
                    { id: 'dimension-dashboards', label: 'Dimension Dashboards' },
                    { id: 'netadds', label: 'Net Adds / Losses' },
                    { id: 'pricing', label: 'Pricing & WTP' },
                    { id: 'sentiment-breakdown', label: 'Sentiment Breakdown' },
                    ...(['06f43089-4e20-4c0f-b8e7-ed050c971caf', 'bea34d6d-c388-4eec-9581-8f56ef4a65a2', '2a6e2085-4ea9-4e62-bd0b-0d39e3aa12e4', '7284a0bd-2ec3-4299-911c-69ff0e2f27bb'].includes(id) ? [
                        { id: 'demographics', label: 'Demographic Evaluator' }
                    ] : []),
                    ...(locationTopicIds.length > 0 ? [
                        { id: 'locations', label: 'Location Insights' }
                    ] : []),
                    { id: 'daily-trackers', label: 'Daily Trackers' },
                    { id: 'satisfaction-reasons', label: 'Satisfaction Reasons' },
                    ...(id === 'c4a9fafd-c868-4999-97ab-18e075b0f92e' ? [
                        { id: 'interest-level', label: 'Interest Level' }
                    ] : [])
                ]
            },
            {
                // Reads measured stream volume (topic_volume_stats) live via the
                // get_topic_volume_series RPC. Deliberately separate from the
                // Post Volume tracker under Daily Trackers, which charts stored
                // posts rather than the observed raw stream.
                id: 'post-volume',
                label: 'Post Volume',
                subsections: [
                    { id: 'post-volume-daily', label: 'Daily Volume' },
                    { id: 'post-volume-comments', label: 'Comment Tracking' }
                ]
            },
            {
                id: 'llm',
                label: 'LLM Recommendations',
                subsections: [
                    { id: 'llm-recommendations', label: 'Net Promoter Score' },
                    { id: 'llm-competitive-comparison', label: 'Competitive Comparison' },
                ]
            },
            {
                id: 'youtube',
                label: 'YouTube Analytics',
                subsections: [
                    { id: 'youtube-analysis-new', label: 'YouTube Analytics (New)' },
                ]
            },
            {
                id: 'competitive',
                label: 'Competitive Landscape',
                subsections: [
                    { id: 'competitive-overview', label: 'Competitive Overview' },
                    { id: 'comp-losses', label: 'Competitive Losses / Wins' },
                    { id: 'competitor-mentions', label: 'Competitor Mentions' },
                    { id: 'nps-comparison', label: 'NPS Comparison' }
                ]
            },
            { 
                id: 'insights', 
                label: 'Research & Insights', 
                subsections: [
                    { id: 'company-chat', label: 'Research Assistant' },
                    { id: 'search', label: 'Search Scuttlebutt' },
                    { id: 'news', label: 'Recent News' },
                    { id: 'twitter', label: 'Search Twitter / X' }
                ]
            },
            {
                id: 'management',
                label: 'Management',
                subsections: [
                    { id: 'profiles', label: 'Profiles' },
                    { id: 'track-record', label: 'Track Record' }
                ]
            },
            {
                id: 'credits',
                label: 'Credits'
            }
        ];

        // Add custom assessment section if available
        if (customAssessmentSection) {
            sections.push(customAssessmentSection);
        }

        // Add interest rates section if Carvana topic exists
        if (companyData.topics?.some(topic => topic.id === 'df359ecc-cf9f-4b9d-b300-ab895bda33fe')) {
            sections.push({
                id: 'interest-rates',
                label: 'Interest Rates'
            });
        }

        return sections;
    }, [companyData.ticker, customAssessmentSection, locationTopicIds]);

    const initializeTopicMap = (topics) => {
        const newTopicMap = {};
        
        topics?.forEach(topic => {
            console.log(`Initializing topic ${topic.topic} with competitors:`, topic.competitors);
            
            newTopicMap[topic.id] = {
                id: topic.id,
                name: topic.topic,
                relationship: topic.relationship,
                priority: topic.priority,
                search_terms: topic.search_terms,
                assessments: {
                    NPS: null,  // Will be populated later
                    pricing: null,  // Will be populated later
                    competitors: null,  // Will be populated later
                    competitiveComparison: null, // Will be populated later
                    leaverJoiner: null, // Will be populated later
                    demographics: null, // Will be populated later
                },
                competitors: (topic.competitors || []).map(comp => ({
                    id: comp.id,
                    topic: comp.topic,
                    additional_text: comp.additional_text,
                    alt_name: comp.alt_name,
                    relationship_type: comp.relationship_type,
                    assessments: {
                        NPS: null,  // Will be populated later
                        pricing: null,  // Will be populated later
                        competitiveComparison: null, // Will be populated later
                        leaverJoiner: null, // Will be populated later
                        demographics: null, // Will be populated later
                    }
                })),
                metadata: {
                    lastUpdated: new Date(),
                    dataLoadStatus: {
                        NPS: 'pending',
                        pricing: 'pending',
                        competitors: 'pending',
                        competitiveComparison: 'pending',
                        leaverJoiner: 'pending',
                        demographics: 'pending'
                    }
                }
            };
            
            console.log(`Initialized topic map entry for ${topic.topic}:`, newTopicMap[topic.id]);
        });
        
        console.log('Initialized Topic Map:', newTopicMap);
        return newTopicMap;
    };

    const fetchCompanyData = async () => {
        if (!userId) {
            console.log('Waiting for user ID before fetching company data...');
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiPost('company', { id: id });

            const data = await response.json();
            
            if (!response.ok) {
                console.error('Server error:', data);
                // Handle 404 (company not found or access denied)
                if (response.status === 404) {
                    // Async cleanup of recently viewed companies
                    validateRecentlyViewedCompanies(apiPost);
                    // Redirect to search with message
                    navigate('/search', {
                        state: { message: 'Company not found.' },
                        replace: true
                    });
                    return;
                }
                throw new Error(data.error || `HTTP error! status: ${response.status}`);
            }
            
            if (data.error) {
                console.error('Error from server:', data.error);
                throw new Error(data.error);
            }
            
            if (data && data.data) {
                console.log('Raw company data from server:', data.data);

                setCompanyData(data.data);
                setOnWatchlist(data.data.is_watchlisted || false);
                
                // Initialize topic map with basic data
                const initialTopicMap = initializeTopicMap(data.data.topics);
                setTopicMap(initialTopicMap);

                // Log the competitors data for each topic
                data.data.topics?.forEach(topic => {
                    console.log(`Topic ${topic.topic} (${topic.id}) competitors:`, topic.competitors);
                });
                
                
                // Only fetch competitive comparisons if we have a userId
                console.log('[DEBUG] Fetching competitive-comparisons for company:', id);
                const compResponse = await apiPost('company/competitive-comparisons', { id: id });
                console.log('[DEBUG] competitive-comparisons response status:', compResponse.status, compResponse.ok);

                if (compResponse.ok) {
                    const compData = await compResponse.json();
                    console.log('[DEBUG] competitive-comparisons raw data:', compData);
                    if (compData.data) {
                        // Handle double-wrapped response from prod: {data: {data: {...}}} vs local: {data: {...}}
                        const actualData = compData.data.data && typeof compData.data.data === 'object' && !Array.isArray(compData.data.data)
                            ? compData.data.data
                            : compData.data;
                        console.log('[DEBUG] competitive-comparisons using keys:', Object.keys(actualData));
                        setCompetitiveComparisons(actualData);
                    }
                } else {
                    console.log('[DEBUG] competitive-comparisons FAILED - status:', compResponse.status);
                }
                
                // Log topics for debugging
                console.log('Topics loaded:', data.data.topics?.length || 0);
                if (data.data.topics) {
                    console.log('Topics with search terms:', data.data.topics.map(t => ({
                        id: t.id,
                        topic: t.topic,
                        search_terms: t.search_terms,
                        competitors: t.competitors
                    })));
                }
            } else {
                console.error('Invalid data structure received:', data);
                throw new Error('Invalid data structure received from server');
            }
        } catch (error) {
            console.error('Error fetching company data:', error);
            setCompanyData({});
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyData();
    }, [userId, token, serverAddress, id]);

    // Reset loaded data types when company changes
    useEffect(() => {
        setLoadedDataTypes({
            nps: false,
            pricing: false,
            leaverJoiner: false,
            competitiveComparison: false,
            demographics: false
        });
    }, [id]);

    useEffect(() => {
        if (companyData.name) {
            document.title = companyData.name;
        }
    }, [companyData.name]);

    // Check which topics have location data (gates the Location Insights tab)
    useEffect(() => {
        const topicIds = Object.keys(topicMap);
        if (topicIds.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await apiPost('topic/locations/available', { topic_ids: topicIds });
                if (!response.ok) return;
                const json = await response.json();
                if (!cancelled) setLocationTopicIds(Object.keys(json.data || {}));
            } catch (err) {
                console.error('Error checking location data availability:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [topicMap]);

    // Lazy load NPS data when viewing NPS section
    useEffect(() => {
        if (activeSection === 'nps' && !loadedDataTypes.nps && Object.keys(topicMap).length > 0) {
            setLoadedDataTypes(prev => ({ ...prev, nps: true }));
            Object.keys(topicMap).forEach(topicId => {
                fetchTopicNPSData(topicId, topicMap);
            });
        }
    }, [activeSection, loadedDataTypes.nps, topicMap]);

    // Lazy load pricing data when viewing pricing section
    useEffect(() => {
        if (activeSection === 'pricing' && !loadedDataTypes.pricing && Object.keys(topicMap).length > 0) {
            setLoadedDataTypes(prev => ({ ...prev, pricing: true }));
            Object.keys(topicMap).forEach(topicId => {
                fetchTopicPricingData(topicId, topicMap);
            });
        }
    }, [activeSection, loadedDataTypes.pricing, topicMap]);

    // Lazy load leaver-joiner data when viewing netadds section
    useEffect(() => {
        if (activeSection === 'netadds' && !loadedDataTypes.leaverJoiner && Object.keys(topicMap).length > 0) {
            setLoadedDataTypes(prev => ({ ...prev, leaverJoiner: true }));
            Object.keys(topicMap).forEach(topicId => {
                fetchTopicLeaverJoinerData(topicId, topicMap);
            });
        }
    }, [activeSection, loadedDataTypes.leaverJoiner, topicMap]);

    // Lazy load competitive comparison data when viewing comp-losses or competitive-overview section
    useEffect(() => {
        if ((activeSection === 'comp-losses' || activeSection === 'competitive-overview') && !loadedDataTypes.competitiveComparison && Object.keys(topicMap).length > 0) {
            setLoadedDataTypes(prev => ({ ...prev, competitiveComparison: true }));
            Object.keys(topicMap).forEach(topicId => {
                fetchTopicCompetitiveComparisonData(topicId, topicMap);
            });
        }
    }, [activeSection, loadedDataTypes.competitiveComparison, topicMap]);


    const handleWatchlist = async () => {
        const newWatchlistStatus = !onWatchlist;
        setOnWatchlist(newWatchlistStatus);

        await apiPost('toggle-watchlist', {
            company_id: id,
            on_watchlist: !newWatchlistStatus,
        });
    };

    const MIN_NEWS_RELEVANCE = 5;

    const getAllRelevantPosts = async () => {
        try {
            console.log('Fetching all relevant posts for company:', id);
            const response = await apiPost('company/all-relevant-posts', { company_id: id });

            if (!response.ok) {
                throw new Error('Failed to fetch all relevant posts');
            }

            const data = await response.json();
            console.log('Received all relevant posts:', data);
            setAllRelevantPosts(data.data || []);
        } catch (error) {
            console.error('Error fetching all relevant posts:', error);
            setAllRelevantPosts([]);
        }
    };

    useEffect(() => {
        console.log('All relevant posts:', allRelevantPosts);
    }, [allRelevantPosts]);

    useEffect(() => {
        if (id && activeSection === 'news') {
            getAllRelevantPosts();
        }
    }, [id, activeSection]);

    const renderMainContent = () => {
        const allPosts = companyData.topics?.flatMap(topic => topic.posts) || [];

        // Redirect to overview if trying to access financial sections for non-public company
        if (!companyData.ticker && ['financials', 'earnings', 'filings', 'transcripts'].includes(activeSection)) {
            handleSectionChange('overview');
            return null;
        }

        const getRelevantNews = () => {
            if (!Array.isArray(allPosts)) return [];
            
            return allPosts
                .filter(post => 
                    post?.post_assessments?.some(assessment => 
                        assessment?.type === 'relevance' && 
                        assessment?.rating >= MIN_NEWS_RELEVANCE
                    )
                )
                .sort((a, b) => (b?.time || 0) - (a?.time || 0));
        };

        switch (activeSection) {
            case 'overview':
            // case 'financials':
            // case 'filings':
                return (
                    <div className="company-overview">
                        <div className="overview-content">
                            {activeSection === 'overview' && (
                                <>
                                    <div className="overview-grid">
                                        <div className="overview-left">
                                            <ContentCard title="Company Overview">
                                                {companyData.description ? (
                                                    <p>{companyData.description}</p>
                                                ) : (
                                                    <NoDataMessage 
                                                        topic="company overview"
                                                        companyName={companyData.name}
                                                        companyId={id}
                                                        companyTicker={companyData.ticker}
                                                        userId={userId}
                                                        dataType="COMPANY_OVERVIEW"
                                                    />
                                                )}
                                            </ContentCard>
                                            {/* {companyData.ticker && (
                                                <ContentCard>
                                                    <div className="stock-chart-header">
                                                        <TimeRangeSelector
                                                            timeRange={stockChartTimeRange}
                                                            onTimeRangeChange={handleStockChartTimeRangeChange}
                                                            periods={['1y', '3y', '5y', '10y', 'all']}
                                                            defaultPeriod="3y"
                                                        />
                                                    </div>
                                                    <StockChartContainer 
                                                        symbol={companyData.ticker} 
                                                        timePeriod={stockChartTimeRange} 
                                                    />
                                                </ContentCard>
                                            )} */}
                                        </div>
                                        <div className="overview-right">
                                            <ContentCard 
                                                title={
                                                    <div className="card-header-with-action">
                                                        <h2 className="content-title">Recent Scuttlebutt</h2>
                                                        <button 
                                                            className="view-all-button"
                                                            onClick={() => handleSectionChange('news')}
                                                        >
                                                            View All <ArrowForwardIcon />
                                                        </button>
                                                    </div>
                                                }
                                                variant="headerOnly"
                                            >
                                                <div className="scuttlebutt-content-wrapper">
                                                    {console.log('Current relevant posts:', relevantPosts)}
                                                    {relevantPosts.length > 0 ? (
                                                        <FilteredPostsDisplay 
                                                            posts={relevantPosts}
                                                            hideHeader={true}
                                                            showBookmarkButton={false}
                                                            disablePagination={false}
                                                            initialPostCount={10}
                                                            linkTo="/news"
                                                            linkText="View More Scuttlebutt"
                                                            showSortControls={true}
                                                            defaultSortMethod="mostRelevant"
                                                            ratingDisplayName="Relevance"
                                                        />
                                                    ) : (
                                                        <NoDataMessage 
                                                            topic="recent scuttlebutt"
                                                            companyName={companyData.name}
                                                            companyId={id}
                                                            companyTicker={companyData.ticker}
                                                            userId={userId}
                                                            dataType="SCUTTLEBUTT"
                                                        />
                                                    )}
                                                </div>
                                            </ContentCard>
                                        </div>
                                    </div>
                                </>
                            )}
                            {/* {activeSection === 'financials' && (
                                <ContentCard title="Financial Performance" variant="headerOnly">
                                    {companyData.ticker ? (
                                        <FundamentalsChart symbol={companyData.ticker} />
                                    ) : (
                                        <NoDataMessage 
                                            topic="financial data"
                                            companyName={companyData.name}
                                            companyId={id}
                                            companyTicker={companyData.ticker}
                                            userId={userId}
                                            dataType="FINANCIAL_DATA"
                                        />
                                    )}
                                </ContentCard>
                            )} */}
                            {/* {activeSection === 'filings' && (
                                <div>
                                    {filings ? (
                                        <FilingsTable filings={filings} companyData={companyData} />
                                    ) : (
                                        <NoDataMessage 
                                            topic="company filings"
                                            companyName={companyData.name}
                                            companyId={id}
                                            companyTicker={companyData.ticker}
                                            userId={userId}
                                            dataType="SEC_FILINGS"
                                        />
                                    )}
                                </div>
                            )} */}
                        </div>
                    </div>
                );
            case 'netadds':
                return (
                    <ContentCard title="Net Adds and Losses">
                        {true ? (
                            <LeaverJoinerPlot
                                posts={allPosts}
                                stat_name="leaver_joiner"
                                titles={["Customer Churn", "Competitor Churn", "Competitive Analysis"]}
                                product_name={companyData.name}
                                topics={Object.values(topicMap)}
                                assessmentData={
                                    // Get leaver-joiner data from all topics in the topicMap
                                    Object.values(topicMap)
                                        .filter(topic => topic.assessments?.leaverJoiner)
                                        .flatMap(topic => topic.assessments.leaverJoiner || [])
                                }
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        ) : (
                            <NoDataMessage
                                topic="customer churn"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="CUSTOMER_CHURN"
                            />
                        )}
                    </ContentCard>
                );
            case 'nps':
                return (
                    <ContentCard title="Estimated Net Promoter Score">
                        {true ? (
                            <NPSPage
                                topicMap={topicMap}
                                companyTicker={companyData.ticker}
                                onLoadCompetitors={fetchNPSCompetitorData}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        ) : (
                            <NoDataMessage
                                topic="NPS data"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="NPS_DATA"
                            />
                        )}
                    </ContentCard>
                );
            case 'dimension-nps':
                return (
                    <ContentCard title="NPS by Dimension">
                        <DimensionNPSPage
                            topicMap={topicMap}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'llm-recommendations':
                return (
                    <ContentCard title="LLM Recommendations — Net Promoter Score">
                        <LLMRecommendationsPage topicMap={topicMap} />
                    </ContentCard>
                );
            case 'llm-competitive-comparison':
                return (
                    <ContentCard title="LLM Recommendations — Competitive Comparison">
                        <LLMCompetitiveComparisonPage topicMap={topicMap} />
                    </ContentCard>
                );
            // Old YouTube Analytics retired (2026-09) — no longer in the nav.
            // The old section key falls through to the New page so any stale
            // bookmarks/URLs land there gracefully. Old component preserved at
            // components/YouTubeAnalytics.js for reference.
            case 'youtube-analysis':
            case 'youtube-analysis-new':
                return (
                    <ContentCard title="YouTube Analytics (New)">
                        <YouTubeAnalyticsNew
                            topics={Object.values(topicMap)}
                            companyName={companyData.name}
                            companyTicker={companyData.ticker}
                        />
                    </ContentCard>
                );
            case 'dimension-dashboards':
                return (
                    <ContentCard title="Dimension Dashboards">
                        <DimensionDashboardsComposed
                            topicMap={topicMap}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'comp-losses':
                return (
                    <ContentCard title="Competitive Losses / Wins">
                        {true ? (
                            <CompetitorChurnPlot
                                posts={allPosts}
                                product_name={companyData.name}
                                topics={Object.values(topicMap)}
                                assessmentData={competitiveAssessmentData}
                            />
                        ) : (
                            <NoDataMessage 
                                topic="competitive loss data"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="COMPETITIVE_LOSSES"
                            />
                        )}
                    </ContentCard>
                );
            case 'competitor-mentions':
                return (
                    <ContentCard title="Competitor Mentions">
                        {true ? (
                            <CompetitorMentionsChart 
                                topics={companyData.topics?.filter(topic => 
                                    topic.relationship === "product"
                                ) || []}
                                assessmentData={competitiveAssessmentData}
                            />
                        ) : (
                            <NoDataMessage 
                                topic="competitor mentions data"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="COMPETITOR_MENTIONS"
                            />
                        )}
                    </ContentCard>
                );
            case 'competitive-overview':
                return (
                    <CompetitiveDifferentiators 
                        topics={companyData.topics}
                        allPosts={allPosts}
                        hasTopicData={true}
                        companyName={companyData.name}
                        companyId={id}
                        companyTicker={companyData.ticker}
                        userId={userId}
                        competitiveComparisons={competitiveComparisons}
                        assessmentData={competitiveAssessmentData}
                    />
                );
            case 'pricing':
                return (
                    <ContentCard title="Pricing & Willingness to Pay">
                        <PricingTrendChart
                            topicMap={topicMap}
                            companyName={companyData.name}
                        />
                        <PricingPlot
                            posts={allPosts}
                            title="Price Sensitivity Analysis"
                            topics={companyData.topics?.filter(topic => 
                                topic.posts?.some(post => 
                                    post.post_assessments?.some(assessment => 
                                        assessment.type === 'pricing'
                                    )
                                )
                            )}
                            assessmentData={
                                // Get pricing data from all topics in the topicMap
                                Object.values(topicMap)
                                    .filter(topic => topic.assessments?.pricing)
                                    .flatMap(topic => topic.assessments.pricing || [])
                            }
                            topicMap={topicMap}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'nps-comparison':
                return (
                    <ContentCard title="NPS Comparison">
                        <NPSComparisonChart 
                            topicMap={topicMap}
                            stat_name="NPS"
                        />
                    </ContentCard>
                );
            // case 'products':
            //     return (
            //         <ContentCard title="Products">
            //             {products.length > 0 ? (
            //                 <div className="products-section">
            //                     {products.map((product, index) => (
            //                         <div key={index} className="card card-md product-card">
            //                             <div className="product-header">
            //                                 <h3 className="product-name">{product.topic}</h3>
            //                             </div>
            //                             {product.additional_text && (
            //                                 <div className="product-description">
            //                                     <p>{product.additional_text}</p>
            //                                 </div>
            //                             )}
            //                         </div>
            //                     ))}
            //                 </div>
            //             ) : (
            //                 <NoDataMessage 
            //                     topic="product information"
            //                     companyName={companyData.name}
            //                     companyId={id}
            //                     companyTicker={companyData.ticker}
            //                     userId={userId}
            //                     dataType="PRODUCT_INFO"
            //                 />
            //             )}
            //         </ContentCard>
            //     );
            case 'twitter':
                return (
                    <CompanyTwitterSearch 
                        companyName={companyData.name}
                        ticker={companyData.ticker}
                    />
                );
            // case 'transcripts':
            //     return (
            //         <ContentCard title="Earnings Call Transcripts">
            //             <TranscriptDisplay 
            //                 companyData={companyData}
            //                 filings={filings}
            //             />
            //         </ContentCard>
            //     );
            // case 'earnings':
            //     return (
            //         <ContentCard title="Earnings History">
            //             {companyData.ticker ? (
            //                 <EarningsHistory 
            //                     ticker={companyData.ticker}
            //                     flaskServerAddress={flaskServerAddress}
            //                     companyData={companyData}
            //                     filings={filings}
            //                 />
            //             ) : (
            //                 <NoDataMessage 
            //                     topic="earnings data"
            //                     companyName={companyData.name}
            //                     companyId={id}
            //                     companyTicker={companyData.ticker}
            //                     userId={userId}
            //                     dataType="EARNINGS_DATA"
            //                 />
            //             )}
            //         </ContentCard>
            //     );
            case 'news':
                return (
                    <ContentCard title="Recent Scuttlebutt" variant="headerOnly">
                        {allRelevantPosts.length > 0 ? (
                            <FilteredPostsDisplay 
                                posts={allRelevantPosts}
                                hideHeader={true}
                                showBookmarkButton={true}
                                disablePagination={false}
                                showSortControls={true}
                                defaultSortMethod="newest"
                            />
                        ) : (
                            <NoDataMessage 
                                topic="recent scuttlebutt"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="SCUTTLEBUTT"
                            />
                        )}
                    </ContentCard>
                );
            case 'search':
                return (
                    <ContentCard title="Search Scuttlebutt" variant="headerOnly">
                        <CompanySearch 
                            topics={companyData.topics} 
                            flaskServerAddress={flaskServerAddress}
                        />
                    </ContentCard>
                );
            case 'company-chat':
                return (
                    <ContentCard title="Research Assistant" variant="headerOnly">
                        <CompanyChat companyId={id} />
                    </ContentCard>
                );
            // Add new cases for custom assessments
            case 'custom-price_difference':
                return (
                    <ContentCard title="Price Difference Analysis">
                        {hasCustomAssessmentData(allPosts, 'price_difference') ? (
                            <CustomAssessmentDisplay 
                                data={getCustomAssessmentData(allPosts, 'price_difference')}
                                metricName="Price Difference"
                                description="Analysis of price differences between Carvana and local dealerships"
                                ratingFormat="currency"
                            />
                        ) : (
                            <NoDataMessage 
                                topic="price difference analysis"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="PRICE_DIFFERENCE"
                            />
                        )}
                    </ContentCard>
                );
            case 'custom-dealership_tactics':
                return (
                    <ContentCard title="Dealership Tactics Analysis">
                        {hasCustomAssessmentData(allPosts, 'dealership_tactics') ? (
                            <CustomAssessmentDisplay 
                                data={getCustomAssessmentData(allPosts, 'dealership_tactics')}
                                metricName="Dealership Tactics"
                                description="Analysis of aggressive dealership tactics against Carvana's offers"
                            />
                        ) : (
                            <NoDataMessage 
                                topic="dealership tactics analysis"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="DEALERSHIP_TACTICS"
                            />
                        )}
                    </ContentCard>
                );
            case 'custom-customer_preference':
                return (
                    <ContentCard title="Customer Preference Analysis">
                        {hasCustomAssessmentData(allPosts, 'customer_preference') ? (
                            <CustomAssessmentDisplay 
                                data={getCustomAssessmentData(allPosts, 'customer_preference')}
                                metricName="Customer Preference"
                                description="Analysis of customer preference for Carvana over local dealerships"
                            />
                        ) : (
                            <NoDataMessage 
                                topic="customer preference analysis"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="CUSTOMER_PREFERENCE"
                            />
                        )}
                    </ContentCard>
                );
            case 'credits':
                return (
                    <ContentCard title="Credits">
                        <CreditsOverview />
                    </ContentCard>
                );
            case 'interest-rates':
                return (
                    <ContentCard title="Interest Rate Analysis">
                        {isLoadingInterestRates ? (
                            <div className="progress-indicator"><CircularProgress /></div>
                        ) : interestRateData ? (
                            <InterestRateDisplay data={interestRateData} />
                        ) : (
                            <NoDataMessage 
                                topic="interest rate data"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="INTEREST_RATES"
                            />
                        )}
                    </ContentCard>
                );
            case 'interest-level':
                const handleClosePostsReferenced = () => {
                    setShowPostDisplay(false);
                    setPostsReferenced([]);
                };

                return (
                    <ContentCard>
                        {isLoadingInterestChange ? (
                            <div className="progress-indicator"><CircularProgress /></div>
                        ) : interestChangeData.length > 0 ? (
                            <InterestLevelChart 
                                data={interestChangeData}
                                onPostsSelected={(posts, timePeriod) => {
                                    setPostsReferenced(posts);
                                    setSelectedTimePeriod(timePeriod);
                                    setShowPostDisplay(true);
                                }}
                                selectedTimePeriod={selectedTimePeriod}
                            />
                        ) : (
                            <NoDataMessage 
                                topic="interest level data"
                                companyName={companyData.name}
                                companyId={id}
                                companyTicker={companyData.ticker}
                                userId={userId}
                                dataType="INTEREST_LEVEL"
                            />
                        )}
                        {showPostDisplay && postsReferenced.length > 0 && (
                            <FilteredPostsDisplay 
                                posts={postsReferenced}
                                onClose={handleClosePostsReferenced}
                                id="interest-level-posts"
                                title={selectedTimePeriod?.label || "Referenced Posts"}
                                ratingDisplayName="Interest Change"
                                autoScroll={true}
                                ratingColorScheme={{ high: 7, low: 4 }}
                                ratingFormat="integer"
                            />
                        )}
                    </ContentCard>
                );
            case 'sentiment-breakdown':
                return (
                    <ContentCard title="Sentiment Breakdown">
                        <CustomSentimentBreakdown
                            topicMap={topicMap}
                            companyName={companyData.name}
                            companyTicker={companyData.ticker}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'demographics':
                return (
                    <ContentCard title="Demographic Evaluator">
                        <DemographicEvaluator
                            topicMap={topicMap}
                            companyName={companyData.name}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'locations':
                return (
                    <ContentCard title="Location Insights">
                        <LocationInsights
                            topicMap={locationTopicMap}
                            companyName={companyData.name}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            case 'post-volume-daily':
                return (
                    <ContentCard title="Post Volume — Daily">
                        <PostVolumePanel
                            topicMap={topicMap}
                            companyName={companyData.name}
                            view="daily"
                        />
                    </ContentCard>
                );
            case 'post-volume-comments':
                return (
                    <ContentCard title="Post Volume — Comment Tracking">
                        <PostVolumePanel
                            topicMap={topicMap}
                            companyName={companyData.name}
                            view="comments"
                        />
                    </ContentCard>
                );
            case 'daily-trackers':
                return (
                    <div style={{ padding: '24px', maxWidth: '100%', boxSizing: 'border-box' }}>
                        {/* Section Label */}
                        <div style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '1.5px',
                            marginBottom: '12px'
                        }}>
                            DAILY TRACKERS
                        </div>

                        {/* Tracker Type Selector */}
                        <div style={{ marginBottom: '20px' }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select
                                    value={dailyTrackerType}
                                    onChange={(e) => setDailyTrackerType(e.target.value)}
                                    sx={{
                                        height: 36,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d0d0',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#999',
                                        },
                                    }}
                                >
                                    <MenuItem value="leaver-joiner">Net Adds / Losses</MenuItem>
                                    <MenuItem value="post-volume">Post Volume</MenuItem>
                                    <MenuItem value="post-likes">Post Likes</MenuItem>
                                    <MenuItem value="post-engagement">Post Engagement (New)</MenuItem>
                                    <MenuItem value="engagement-sentiment">Engagement by Sentiment (New)</MenuItem>
                                    <MenuItem value="post-views">Post Views (New)</MenuItem>
                                    <MenuItem value="views-sentiment">Views by Sentiment (New)</MenuItem>
                                    <MenuItem value="nps-rolling">NPS Rolling Average</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        {/* Render selected tracker */}
                        {dailyTrackerType === 'leaver-joiner' && (
                            <DailyLeaverJoiner
                                topicMap={topicMap}
                                companyName={companyData.name}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'post-volume' && (
                            <PostAnalytics
                                topicMap={topicMap}
                                companyName={companyData.name}
                                companyTicker={companyData.ticker}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'post-likes' && (
                            <PostLikesAnalytics
                                topicMap={topicMap}
                                companyName={companyData.name}
                                companyTicker={companyData.ticker}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'post-engagement' && (
                            <PostEngagementAnalytics
                                compactYAxis
                                topicMap={topicMap}
                                companyName={companyData.name}
                                companyTicker={companyData.ticker}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'engagement-sentiment' && (
                            <PostSentimentEngagementAnalytics
                                compactYAxis
                                topicMap={topicMap}
                                companyName={companyData.name}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'post-views' && (
                            <PostEngagementAnalytics
                                compactYAxis
                                topicMap={topicMap}
                                companyName={companyData.name}
                                companyTicker={companyData.ticker}
                                metric="views"
                                chartTitle="Post Views (New)"
                                valueAxisLabel="Number of Views"
                                barName="View Count"
                                note="Views are only available for YouTube and TikTok posts."
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'views-sentiment' && (
                            <PostSentimentEngagementAnalytics
                                compactYAxis
                                topicMap={topicMap}
                                companyName={companyData.name}
                                metric="views"
                                chartTitle="Views by Sentiment"
                                note="Views are only available for YouTube and TikTok posts."
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                        {dailyTrackerType === 'nps-rolling' && (
                            <NPSMovingAverage
                                topicMap={topicMap}
                                companyName={companyData.name}
                                selectedProduct={sharedSelectedTopic}
                                onProductChange={setSharedSelectedTopic}
                            />
                        )}
                    </div>
                );
            case 'satisfaction-reasons':
                return (
                    <ContentCard title="Satisfaction Reasons">
                        <SatisfactionReasons
                            topicMap={topicMap}
                            companyName={companyData.name}
                            selectedProduct={sharedSelectedTopic}
                            onProductChange={setSharedSelectedTopic}
                        />
                    </ContentCard>
                );
            default:
                return (
                    <ContentCard title="Section Under Development">
                        <div>This section is currently under development.</div>
                    </ContentCard>
                );
        }
    };

    // Handle main section click
    const handleMainSectionClick = (sectionId) => {
        if (sectionId === 'overview' || sectionId === 'credits' || sectionId === 'interest-rates') {
            setFocusedItem(sectionId);
            handleSectionChange(sectionId);
        }
    };

    useEffect(() => {
        const fetchFilings = async () => {
            try {
                console.log('Attempting to fetch filings with CIK:', companyData.cik);
                const response = await fetch(flaskServerAddress + `filings/${companyData.cik}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                });
                const data = await response.json();
                console.log('Raw filings data from server:', data);
                console.log('Sample filing from 8-K:', data.categories?.['8-K']?.filings?.[0]);
                setFilings(data);
            } catch (error) {
                console.error('Error fetching filings:', error);
            }
        };
        
        if (companyData.cik && userId && token) {
            console.log('Conditions met for fetching filings');
            fetchFilings();
        }
    }, [companyData.cik, userId, token, flaskServerAddress]);

    useEffect(() => {
        if (companyData.topics) {
            const prod = companyData.topics
                ?.filter(topic => topic.relationship === "product")
                .sort((a, b) => {
                    // Handle undefined priorities - put them at the end
                    if (a.priority === undefined && b.priority === undefined) return 0;
                    if (a.priority === undefined) return 1;
                    if (b.priority === undefined) return -1;
                    return a.priority - b.priority;
                }) || [];
            console.log('Products:', prod);
            setProducts(prod);
        }
    }, [companyData]);

    const getAllNavigableItems = useCallback(() => {
        const flattenedItems = sidebarSections.reduce((acc, section) => {
            if (section.id === 'overview') {
                acc.push({ id: section.id, label: section.label });
            }
            section.subsections?.forEach(subsection => {
                acc.push({ id: subsection.id, label: subsection.label });
            });
            return acc;
        }, []);
        return flattenedItems;
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!focusedItem) return;

        const items = getAllNavigableItems();
        const currentIndex = items.findIndex(item => item.id === focusedItem);

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                if (currentIndex > 0) {
                    const prevItem = items[currentIndex - 1];
                    setFocusedItem(prevItem.id);
                    handleSectionChange(prevItem.id);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (currentIndex < items.length - 1) {
                    const nextItem = items[currentIndex + 1];
                    setFocusedItem(nextItem.id);
                    handleSectionChange(nextItem.id);
                }
                break;
            default:
                break;
        }
    }, [focusedItem, getAllNavigableItems]);

    useEffect(() => {
        const sidebarElement = sidebarRef.current;
        if (sidebarElement) {
            sidebarElement.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            if (sidebarElement) {
                sidebarElement.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [handleKeyDown]);

    const fetchTopicNPSData = async (topicId, currentTopicMap) => {
        if (!userId) return;

        console.log(`Starting fetch of NPS data for topic ${topicId}...`);
        try {
            // Fetch pre-calculated Bayesian NPS data from backend API
            // Main topic only - competitors loaded separately via fetchNPSCompetitorData
            const response = await apiPost('topic/bayesian-nps', {
                topic_ids: [topicId],
                months_back: 60
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            console.log(`Received NPS data for topic ${topicId}:`, data?.length, 'months');

            // Transform RPC response to match expected format
            // Data comes as: { topic_id, month, nps, lower_ci, upper_ci, sample_size, is_forward_filled }
            const npsResults = data?.map(row => ({
                month: row.month,
                meanNPS: row.nps,
                lowerCI: row.lower_ci,
                upperCI: row.upper_ci,
                count: row.sample_size,
                isForwardFilled: row.is_forward_filled
            })) || [];

            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];

                if (topicData) {
                    const updatedMap = {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            // Store pre-calculated NPS results
                            npsResults: npsResults,
                            metadata: {
                                ...topicData.metadata,
                                lastUpdated: new Date(),
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    NPS: 'loaded'
                                }
                            }
                        }
                    };
                    console.log('Updated Topic Map with NPS data for topic', topicId);
                    return updatedMap;
                }

                return prevMap;
            });
        } catch (error) {
            console.error(`Error fetching NPS data for topic ${topicId}:`, error);
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];

                if (topicData) {
                    return {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            metadata: {
                                ...topicData.metadata,
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    NPS: 'error'
                                }
                            }
                        }
                    };
                }
                return prevMap;
            });
        }
    };

    // Fetch NPS data for competitors of all topics (called when user clicks "Show Competitors")
    const fetchNPSCompetitorData = async () => {
        if (!userId) return;

        console.log('Starting fetch of NPS competitor data...');

        // Get all main topic IDs
        const mainTopicIds = Object.keys(topicMap);

        for (const topicId of mainTopicIds) {
            const topic = topicMap[topicId];

            // Skip if no competitors or competitors already have NPS results
            if (!topic.competitors || topic.competitors.length === 0) {
                console.log(`No competitors for topic ${topicId}`);
                continue;
            }

            if (topic.competitors.every(comp => comp.npsResults)) {
                console.log(`NPS data already loaded for all competitors of topic ${topicId}`);
                continue;
            }

            try {
                // Use existing competitor IDs from topicMap (already loaded with company data)
                const competitorIds = topic.competitors.map(c => c.id).filter(id => id);
                console.log(`Found ${competitorIds.length} competitors for topic ${topicId}:`, competitorIds);

                if (competitorIds.length === 0) {
                    console.log(`No valid competitor IDs for topic ${topicId}`);
                    continue;
                }

                // Get NPS data for all competitor IDs from backend API
                const response = await apiPost('topic/bayesian-nps', {
                    topic_ids: competitorIds,
                    months_back: 60
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const npsData = await response.json();

                console.log(`Received NPS data: ${npsData?.length} total rows for competitors of topic ${topicId}`);

                // Group results by topic_id
                const resultsByTopic = {};
                npsData?.forEach(row => {
                    const tid = row.topic_id;
                    if (!resultsByTopic[tid]) {
                        resultsByTopic[tid] = [];
                    }
                    resultsByTopic[tid].push({
                        month: row.month,
                        meanNPS: row.nps,
                        lowerCI: row.lower_ci,
                        upperCI: row.upper_ci,
                        count: row.sample_size,
                        isForwardFilled: row.is_forward_filled
                    });
                });

                setTopicMap(prevMap => {
                    const topicData = prevMap[topicId];

                    if (topicData) {
                        // Update existing competitors with NPS results
                        const updatedCompetitors = topicData.competitors.map(comp => ({
                            ...comp,
                            npsResults: resultsByTopic[comp.id] || null
                        }));

                        return {
                            ...prevMap,
                            [topicId]: {
                                ...topicData,
                                competitors: updatedCompetitors
                            }
                        };
                    }

                    return prevMap;
                });
            } catch (error) {
                console.error(`Error fetching NPS competitor data for topic ${topicId}:`, error);
            }
        }

        console.log('Finished fetching NPS competitor data');
    };

    const fetchTopicCompetitiveComparisonData = async (topicId, currentTopicMap) => {
        if (!userId) return;

        console.log(`Starting fetch of competitive comparison data for topic ${topicId}...`);
        try {
            // Fetch competitive comparison data for the main topic
            const response = await apiPost('topic/competitive-comparisons/cached', { topic_id: topicId });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Received competitive comparison data for topic ${topicId}:`, result.data);

            // Get competitor IDs for this topic
            const competitors = currentTopicMap[topicId]?.competitors || [];

            // Fetch competitive comparison data for all competitors
            const competitorComparisonPromises = competitors.map(async (competitor) => {
                try {
                    console.log(`Starting fetch of competitive comparison data for competitor ${competitor.id}...`);
                    const compResponse = await apiPost('topic/competitive-comparisons/cached', { topic_id: competitor.id });

                    if (!compResponse.ok) {
                        throw new Error(`HTTP error! status: ${compResponse.status}`);
                    }

                    const compResult = await compResponse.json();
                    console.log(`Received competitive comparison data for competitor ${competitor.id}:`, compResult.data);
                    return {
                        id: competitor.id,
                        comparisonData: compResult.data
                    };
                } catch (error) {
                    console.error(`Error fetching competitive comparison data for competitor ${competitor.id}:`, error);
                    return {
                        id: competitor.id,
                        comparisonData: null
                    };
                }
            });

            const competitorComparisonResults = await Promise.all(competitorComparisonPromises);
            
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    // Update competitors with their competitive comparison data
                    const updatedCompetitors = topicData.competitors.map(comp => {
                        const compData = competitorComparisonResults.find(r => r.id === comp.id);
                        return {
                            ...comp,
                            assessments: {
                                ...comp.assessments,
                                competitiveComparison: compData?.comparisonData || null
                            }
                        };
                    });

                    const updatedMap = {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            assessments: {
                                ...topicData.assessments,
                                competitiveComparison: result.data
                            },
                            competitors: updatedCompetitors,
                            metadata: {
                                ...topicData.metadata,
                                lastUpdated: new Date(),
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    competitiveComparison: 'loaded'
                                }
                            }
                        }
                    };
                    console.log('Updated Topic Map with competitive comparison data for topic', topicId);
                    return updatedMap;
                }
                
                return prevMap;
            });
        } catch (error) {
            console.error(`Error fetching competitive comparison data for topic ${topicId}:`, error);
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    return {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            metadata: {
                                ...topicData.metadata,
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    competitiveComparison: 'error'
                                }
                            }
                        }
                    };
                }
                return prevMap;
            });
        }
    };
    
    const fetchTopicLeaverJoinerData = async (topicId, currentTopicMap) => {
        if (!userId) return;

        console.log(`Starting fetch of leaver-joiner data for topic ${topicId}...`);
        try {
            // Fetch leaver-joiner data for the main topic
            const response = await apiPost('topic/leaver-joiner/cached', { topic_id: topicId });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Received leaver-joiner data for topic ${topicId}:`, result.data);

            // Get competitor IDs for this topic
            const competitors = currentTopicMap[topicId]?.competitors || [];

            // Fetch leaver-joiner data for all competitors
            const competitorLeaverJoinerPromises = competitors.map(async (competitor) => {
                try {
                    console.log(`Starting fetch of leaver-joiner data for competitor ${competitor.id}...`);
                    const compResponse = await apiPost('topic/leaver-joiner/cached', { topic_id: competitor.id });

                    if (!compResponse.ok) {
                        throw new Error(`HTTP error! status: ${compResponse.status}`);
                    }

                    const compResult = await compResponse.json();
                    console.log(`Received leaver-joiner data for competitor ${competitor.id}:`, compResult.data);
                    return {
                        id: competitor.id,
                        leaverJoinerData: compResult.data
                    };
                } catch (error) {
                    console.error(`Error fetching leaver-joiner data for competitor ${competitor.id}:`, error);
                    return {
                        id: competitor.id,
                        leaverJoinerData: null
                    };
                }
            });

            const competitorLeaverJoinerResults = await Promise.all(competitorLeaverJoinerPromises);
            
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    // Update competitors with their leaver-joiner data
                    const updatedCompetitors = topicData.competitors.map(comp => {
                        const compData = competitorLeaverJoinerResults.find(r => r.id === comp.id);
                        return {
                            ...comp,
                            assessments: {
                                ...comp.assessments,
                                leaverJoiner: compData?.leaverJoinerData || null
                            }
                        };
                    });

                    const updatedMap = {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            assessments: {
                                ...topicData.assessments,
                                leaverJoiner: result.data
                            },
                            competitors: updatedCompetitors,
                            metadata: {
                                ...topicData.metadata,
                                lastUpdated: new Date(),
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    leaverJoiner: 'loaded'
                                }
                            }
                        }
                    };
                    console.log('Updated Topic Map with leaver-joiner data for topic', topicId);
                    return updatedMap;
                }
                
                return prevMap;
            });
        } catch (error) {
            console.error(`Error fetching leaver-joiner data for topic ${topicId}:`, error);
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    return {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            metadata: {
                                ...topicData.metadata,
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    leaverJoiner: 'error'
                                }
                            }
                        }
                    };
                }
                return prevMap;
            });
        }
    };
    
    const fetchTopicPricingData = async (topicId, currentTopicMap) => {
        if (!userId) return;

        console.log(`Starting fetch of pricing data for topic ${topicId}...`);
        try {
            // Fetch pricing data for the main topic
            const response = await apiPost('topic/pricing/cached', { topic_id: topicId });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Received pricing data for topic ${topicId}:`, result.data);

            // Get competitor IDs for this topic
            const competitors = currentTopicMap[topicId]?.competitors || [];

            // Fetch pricing data for all competitors
            const competitorPricingPromises = competitors.map(async (competitor) => {
                try {
                    console.log(`Starting fetch of pricing data for competitor ${competitor.id}...`);
                    const compResponse = await apiPost('topic/pricing/cached', { topic_id: competitor.id });

                    if (!compResponse.ok) {
                        throw new Error(`HTTP error! status: ${compResponse.status}`);
                    }

                    const compResult = await compResponse.json();
                    console.log(`Received pricing data for competitor ${competitor.id}:`, compResult.data);
                    return {
                        id: competitor.id,
                        pricingData: compResult.data
                    };
                } catch (error) {
                    console.error(`Error fetching pricing data for competitor ${competitor.id}:`, error);
                    return {
                        id: competitor.id,
                        pricingData: null
                    };
                }
            });

            const competitorPricingResults = await Promise.all(competitorPricingPromises);
            
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    // Update competitors with their pricing data
                    const updatedCompetitors = topicData.competitors.map(comp => {
                        const compData = competitorPricingResults.find(r => r.id === comp.id);
                        return {
                            ...comp,
                            assessments: {
                                ...comp.assessments,
                                pricing: compData?.pricingData || null
                            }
                        };
                    });

                    const updatedMap = {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            assessments: {
                                ...topicData.assessments,
                                pricing: result.data
                            },
                            competitors: updatedCompetitors,
                            metadata: {
                                ...topicData.metadata,
                                lastUpdated: new Date(),
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    pricing: 'loaded'
                                }
                            }
                        }
                    };
                    console.log('Updated Topic Map with pricing data for topic', topicId);
                    return updatedMap;
                }
                
                return prevMap;
            });
        } catch (error) {
            console.error(`Error fetching pricing data for topic ${topicId}:`, error);
            setTopicMap(prevMap => {
                const topicData = prevMap[topicId];
                
                if (topicData) {
                    return {
                        ...prevMap,
                        [topicId]: {
                            ...topicData,
                            metadata: {
                                ...topicData.metadata,
                                dataLoadStatus: {
                                    ...topicData.metadata.dataLoadStatus,
                                    pricing: 'error'
                                }
                            }
                        }
                    };
                }
                return prevMap;
            });
        }
    };

    // Add this function to fetch interest rate data
    const fetchInterestRateData = async (topicId) => {
        if (!topicId || !userId) {
            console.log('Missing required data for fetching interest rates:', { topicId, userId });
            return;
        }

        console.log('Fetching interest rate data for topic ID:', topicId);
        setIsLoadingInterestRates(true);
        try {
            const response = await apiPost('topic/interest-rates', { topic_id: topicId });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response from server:', errorText);
                throw new Error('Failed to fetch interest rate data: ' + response.status);
            }

            const responseData = await response.json();
            console.log('Received interest rate data:', responseData);
            setInterestRateData(responseData.data);
        } catch (error) {
            console.error('Error fetching interest rate data:', error);
            setInterestRateData(null);
        } finally {
            setIsLoadingInterestRates(false);
        }
    };

    // Update useEffect to check for Carvana topic
    useEffect(() => {
        if (companyData?.topics && serverAddress && userId) {
            // Find the Carvana topic
            const carvanaTopic = companyData.topics.find(topic => 
                topic.id === 'df359ecc-cf9f-4b9d-b300-ab895bda33fe'
            );
            
            if (carvanaTopic) {
                console.log('Found Carvana topic:', carvanaTopic);
                fetchInterestRateData(carvanaTopic.id);
            }
        }
    }, [companyData?.topics, serverAddress, userId]);

    // Add this function to fetch interest change data
    const fetchInterestChangeData = async (topicId) => {
        if (!topicId || !userId) {
            console.log('Missing required data for fetching interest change:', { topicId, userId });
            return;
        }

        console.log('Fetching interest change data for topic ID:', topicId);
        setIsLoadingInterestChange(true);
        try {
            const response = await apiPost('topic/interest-change', { topic_id: topicId });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response from server:', errorText);
                throw new Error('Failed to fetch interest change data: ' + response.status);
            }

            const responseData = await response.json();
            console.log('Received interest change data:', responseData);
            setInterestChangeData(responseData.data || []);
        } catch (error) {
            console.error('Error fetching interest change data:', error);
            setInterestChangeData([]);
        } finally {
            setIsLoadingInterestChange(false);
        }
    };

    // Add this useEffect to fetch interest change data when needed
    useEffect(() => {
        if (id === 'c4a9fafd-c868-4999-97ab-18e075b0f92e' && companyData?.topics && serverAddress && userId) {
            // Find the Labubu topic
            const labubuTopic = companyData.topics.find(topic => 
                topic.id === '9585e83c-ddac-40eb-8315-da5e1e2d20ac'
            );
            
            if (labubuTopic) {
                console.log('Found Labubu topic:', labubuTopic);
                fetchInterestChangeData(labubuTopic.id);
            }
        }
    }, [id, companyData?.topics, serverAddress, userId]);

    const handleSettingsClick = (event) => {
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    const handleSettingChange = (setting, value) => {
        setChartSettings(prev => ({
            ...prev,
            [setting]: value
        }));
    };

    if (isLoading) return <div className="progress-indicator"><CircularProgress /></div>;

    return (
        <div className="company-page-new">
            <div className="navbar-extension" />
            <div className="company-header">
                <div className="company-info">
                    {companyData.logo && companyData.website && (
                        <a 
                            href={companyData.website.startsWith('http') ? companyData.website : `https://${companyData.website}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="company-logo-link"
                        >
                            {typeof companyData.logo === 'string' && companyData.logo.trim().startsWith('<svg') ? (
                                <div
                                    className="company-logo-svg"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(companyData.logo, {
                                            USE_PROFILES: { svg: true, svgFilters: true }
                                        })
                                    }}
                                />
                            ) : (
                                <img
                                    src={`${companyData.logo}?token=pk_LWbmMF36Rqe5UN5m-r0iFg`}
                                    alt={`${companyData.name} logo`}
                                    className="company-logo"
                                />
                            )}
                        </a>
                    )}
                    <div className="company-text-content">
                        <h1>{companyData.name}</h1>
                        <div className="company-metadata">
                            {companyData.ticker && <span>Ticker: {companyData.ticker}</span>}
                            {companyData.cik && <span>CIK: {companyData.cik}</span>}
                            {companyData.exchange && <span>Exchange: {companyData.exchange}</span>}
                            {companyData.sector && <span>Sector: {companyData.sector}</span>}
                        </div>
                    </div>
                </div>
                <div className="header-actions">
                    <CompanyAdminPanel
                        companyId={id}
                        companyData={companyData}
                        serverAddress={serverAddress}
                        token={token}
                        onTopicCreated={fetchCompanyData}
                        isAdmin={isAdmin}
                    />
                    <button
                        className="watchlist-button"
                        onClick={() => setReportModalOpen(true)}
                        style={{ marginRight: 8 }}
                    >
                        Generate Report
                    </button>
                    <button
                        className={`watchlist-button ${onWatchlist ? 'on-watchlist' : ''}`}
                        onClick={handleWatchlist}
                    >
                        <VisibilityIcon />
                        {onWatchlist ? 'On Watchlist' : 'Add to Watchlist'}
                    </button>
                </div>
            </div>            
            <div className="company-content">
                <nav 
                    className="company-sidebar" 
                    ref={sidebarRef}
                    tabIndex="0"
                >
                    {sidebarSections.map(section => (
                        <div key={section.id} className="sidebar-section">
                            <div 
                                className={`sidebar-item main-section ${
                                    !section.subsections || section.subsections.length === 0 || section.id === 'overview' ? 'clickable' : ''
                                } ${activeSection === section.id ? 'active' : ''} ${
                                    focusedItem === section.id ? 'focused' : ''
                                }`}
                                onClick={() => handleMainSectionClick(section.id)}
                                tabIndex="0"
                            >
                                {section.label}
                            </div>
                            <div className="sidebar-subsections">
                                {section.subsections && section.subsections.map(subsection => (
                                    <div 
                                        key={subsection.id}
                                        className={`sidebar-item ${activeSection === subsection.id ? 'active' : ''} ${
                                            focusedItem === subsection.id ? 'focused' : ''
                                        }`}
                                        onClick={() => handleSectionChange(subsection.id)}
                                        tabIndex="0"
                                    >
                                        {subsection.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>
                
                <main className="company-main-content">
                    {renderMainContent()}
                </main>
            </div>
            <ReportGenerator
                isOpen={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                companyData={companyData}
                companyId={id}
                topicMap={topicMap}
                allPosts={companyData.topics?.flatMap(topic => topic.posts) || []}
            />
        </div>
    );
};

export default CompanyPage; 